/**
 * D75 BUG-1 fix — IndexedDB safety net for in-flight meeting recordings.
 *
 * Problem: the recorder produces an audio Blob that lives ONLY in memory.
 * If the user refreshes (or the network drops) during the upload phase,
 * the Blob is GC'd and the recording is lost forever — even though the
 * backend would have happily accepted it.
 *
 * Solution: the moment recording stops, we write the Blob to a tiny
 * IndexedDB store ("stellar-meeting-uploads") with a generated
 * `pending_id`. We then attempt the network upload as before. On
 * success, we delete the row. On failure (or page reload), the row
 * survives — the MeetingNotesPage scans the store at mount time and
 * surfaces every orphaned recording with a Retry button.
 *
 * Tiny dependency-free API: open(), put(), list(), remove(), clear().
 */

const DB_NAME = 'stellar-meeting-uploads';
const STORE = 'pending';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  });
}

async function _withStore(mode, fn) {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      const out = fn(store);
      tx.oncomplete = () => resolve(out);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (e) {
    // Fall through silently — IndexedDB is a *safety net*, not the
    // critical path. If it's unavailable (private mode, quota, etc.)
    // upload still proceeds normally.
    console.warn('[d75-pending] IndexedDB unavailable:', e?.message);
    return null;
  }
}

/**
 * Stash a Blob under a fresh id. Returns { id } or null on IDB failure.
 */
export async function stashPendingRecording({ blob, mime, ext, userId, workspaceId }) {
  const id = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const row = {
    id,
    blob,
    mime: mime || 'audio/webm',
    ext: ext || 'webm',
    size_bytes: blob?.size || 0,
    user_id: userId || '',
    workspace_id: workspaceId || '',
    recorded_at: new Date().toISOString(),
  };
  await _withStore('readwrite', (s) => s.put(row));
  return id;
}

export async function listPendingRecordings(userId) {
  const all = await _withStore('readonly', (s) => {
    return new Promise((resolve, reject) => {
      const req = s.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
  if (!all) return [];
  const rows = await all; // _withStore returns the fn return value via tx complete
  const arr = Array.isArray(rows) ? rows : [];
  return userId ? arr.filter((r) => !r.user_id || r.user_id === userId) : arr;
}

export async function removePendingRecording(id) {
  await _withStore('readwrite', (s) => s.delete(id));
}

export async function clearAllPending() {
  await _withStore('readwrite', (s) => s.clear());
}
